import type {
  CappedTableResultOfEventLogEntry,
  SuperadminListEventLogsParams
} from 'api/models/api';
import type {
  IpFilterRulesToEdit,
  LoginResult,
  StorageDownloadTemplates,
  SuperadminCompany,
  SuperadminCompanyCodebookItem,
  SuperadminCompanyDetail,
  SuperadminCompanySettingsToEdit,
  SuperadminCompanyToAdd,
  SuperadminCompanyToEdit,
  SuperadminCustomFormTemplate,
  SuperadminCustomFormTemplateToAdd,
  SuperadminCustomFormTemplateToEdit,
  SuperadminLicence,
  SuperadminLicenceToAdd,
  SuperadminNextRevisionSettingsToEdit,
  TableResultSuperadminCompany
} from 'api/models/mainservice';
import type {
  NotificationContentSettings,
  SetCompanyPreferences
} from 'api/models/notificationservice';
import type { SuperadminCompanyStats } from 'api/models/unified';
import {
  notificationHubClient,
  protectedApiClient,
  protectedApiClientV2
} from 'api/support/baseApiClients';
import type {
  NotificationContentScope,
  SetNotificationContentRequest
} from 'components/NotificationsContent/models';
import type { CompanyTableQuery } from 'pages/admin/companies/models';

export default {
  addCompany: async (values: SuperadminCompanyToAdd) => {
    const result = await protectedApiClient.post<SuperadminCompany>(`/superadmin/company`, values);
    return result.data;
  },
  addCustomFormTemplate: async (params: {
    companyId: string;
    data: SuperadminCustomFormTemplateToAdd;
  }) => {
    const result = await protectedApiClient.post<SuperadminCustomFormTemplate>(
      `/superadmin/company/${params.companyId}/customFormTemplate`,
      params.data
    );

    return result.data;
  },
  addLicence: async (data: SuperadminLicenceToAdd) => {
    const result = await protectedApiClient.post<SuperadminLicence[]>(
      `/superadmin/company/licence`,
      data
    );
    return result.data;
  },
  editCompany: async (params: { companyId: string; values: SuperadminCompanyToEdit }) => {
    const result = await protectedApiClient.put<undefined>(
      `/superadmin/company/${params.companyId}`,
      params.values
    );
    return result.data;
  },
  editCustomFormTemplate: async (params: {
    companyId: string;
    templateId: string;
    data: SuperadminCustomFormTemplateToEdit;
  }) => {
    const result = await protectedApiClient.put<SuperadminCustomFormTemplate>(
      `/superadmin/company/${params.companyId}/customFormTemplate/${params.templateId}`,
      params.data
    );

    return result.data;
  },
  getCompanies: async (params: CompanyTableQuery) => {
    const result = await protectedApiClient.get<TableResultSuperadminCompany>(
      `/superadmin/company`,
      {
        params
      }
    );

    return result.data;
  },
  getCompanyDetail: async (companyId: string) => {
    const result = await protectedApiClient.get<SuperadminCompanyDetail>(
      `/superadmin/company/${companyId}`
    );
    return result.data;
  },
  updateCompanySettings: async (params: {
    settings: Partial<SuperadminCompanySettingsToEdit>;
    companyId: string;
  }) => {
    const result = await protectedApiClient.patch<undefined>(
      `/superadmin/company/${params.companyId}`,
      params.settings
    );

    return result.data;
  },
  editNextRevisionSettings: async (params: {
    companyId: string;
    data: SuperadminNextRevisionSettingsToEdit;
  }) => {
    const result = await protectedApiClient.patch<undefined>(
      `/superadmin/company/${params.companyId}/nextRevisionSettings`,
      params.data
    );

    return result.data;
  },
  deleteCustomFormTemplate: async (params: { companyId: string; templateId: string }) => {
    const result = await protectedApiClient.delete<undefined>(
      `/superadmin/company/${params.companyId}/customFormTemplate/${params.templateId}`
    );

    return result.data;
  },
  deleteAllCustomFormTemplates: async (companyId: string) => {
    const result = await protectedApiClient.delete<undefined>(
      `/superadmin/company/${companyId}/customFormTemplate`
    );

    return result.data;
  },
  getCompanyEvents: async (params: SuperadminListEventLogsParams) => {
    const { companyId, ...queryParams } = params;

    const result = await protectedApiClientV2.get<CappedTableResultOfEventLogEntry>(
      `/superadmin/companies/${companyId}/audit/event-logs`,
      {
        params: queryParams
      }
    );
    return result.data;
  },
  toggleLicenceActive: async (licenceId: string) => {
    const result = await protectedApiClient.put<undefined>(
      `/superadmin/company/licence/toggle/${licenceId}`
    );
    return result.data;
  },
  signInAs: (companyId: string, userId: string) => {
    return protectedApiClient.get<LoginResult>(
      `superadmin/company/${companyId}/signInAs/${userId}`
    );
  },
  disableCompany: async (companyId: string) => {
    const result = await protectedApiClient.put<undefined>(
      `/superadmin/company/${companyId}/disable`
    );
    return result.data;
  },
  enableCompany: async (companyId: string) => {
    const result = await protectedApiClient.put<undefined>(
      `/superadmin/company/${companyId}/enable`
    );
    return result.data;
  },
  deleteCompany: async (companyId: string) => {
    const result = await protectedApiClient.delete<undefined>(`/superadmin/company/${companyId}`);
    return result.data;
  },
  getNotifications: async (companyId: string) => {
    const result = await notificationHubClient.get<NotificationContentSettings>(
      `/superadmin/settings/${companyId}`
    );

    return result.data;
  },
  editCompanyPreferences: async (params: {
    companyId: string;
    preferences: SetCompanyPreferences;
  }) => {
    const result = await notificationHubClient.patch<undefined>(
      `/superadmin/settings/${params.companyId}`,
      params.preferences
    );

    return result.data;
  },
  editNotification: async (params: {
    companyId: string;
    scope: NotificationContentScope;
    notificationId: string;
    // Using local interface, generated with apigen is incorrect because of .Net core version
    values: SetNotificationContentRequest;
  }) => {
    const result = await notificationHubClient.patch<undefined>(
      `/superadmin/settings/${params.companyId}/${params.scope}/${params.notificationId}`,
      params.values
    );

    return result.data;
  },
  deleteStamp: async (companyId: string) => {
    const result = await protectedApiClient.delete<SuperadminCompanyDetail>(
      `/superadmin/company/${companyId}/deleteStamp`
    );
    return result.data;
  },
  deleteLogo: async (companyId: string) => {
    const result = await protectedApiClient.delete<SuperadminCompanyDetail>(
      `/superadmin/company/${companyId}/deleteLogo`
    );
    return result.data;
  },
  deleteHeaderLogo: async (companyId: string) => {
    const result = await protectedApiClient.delete<SuperadminCompanyDetail>(
      `/superadmin/company/${companyId}/deleteHeaderLogo`
    );
    return result.data;
  },
  updateCustomFormPlannedInterventions: async (params: {
    companyId: string;
    templateId: string;
  }) => {
    const result = await protectedApiClient.put<undefined>(
      `/superadmin/company/${params.companyId}/customFormTemplate/${params.templateId}/updatePlannedInterventions`
    );

    return result.data;
  },
  updatePlannedInterventions: async (params: { companyId: string }) => {
    const result = await protectedApiClient.put<undefined>(
      `/superadmin/company/${params.companyId}/customFormTemplate/updatePlannedInterventions`
    );

    return result.data;
  },
  getCompaniesCodebook: async () => {
    const result = await protectedApiClient.get<SuperadminCompanyCodebookItem[]>(
      `/superadmin/company/codebook`
    );

    return result.data;
  },
  getCompanyDownloadUriTemplates: async (companyId: string) => {
    const result = await protectedApiClient.get<StorageDownloadTemplates>(
      `/superadmin/company/${companyId}/storageDownloadTemplates`
    );

    return result.data;
  },
  getCompanyStatistics: async (companyId: string) => {
    const result = await protectedApiClient.get<SuperadminCompanyStats>(
      `/superadmin/Statistics/v2/company/${companyId}`
    );

    return result.data;
  },
  editIpFilterRules: async (params: { companyId: string; data: IpFilterRulesToEdit }) => {
    const result = await protectedApiClient.patch<undefined>(
      `/superadmin/company/${params.companyId}/settings/ipFilterRules`,
      params.data
    );

    return result.data;
  }
};
